import React, { useMemo } from 'react';
import type * as monaco from 'monaco-editor';
import { Button, FormControl, IconButton, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import type { TFunction } from 'i18next';
import type { FrameworkAction, FrameworkArgument, FrameworkClass } from '../../../framework/framework.d';
import type { EnumOptionsMap } from '../../../framework/framework-enums';
import DriveUnitPortControl from '../../CodeEditor/Devices/Controls/DriveUnitPortControl';
import UnsupportedCodeStatus from '../../CodeEditor/Devices/Controls/UnsupportedCodeStatus';
import { isUnsupportedSequenceTupleLiteral } from '../pythonArgParser';
import {
  getFrameworkNumericArgError,
  getFrameworkNumericArgRangeText,
  isDriveUnitPortFrameworkArgument,
  isFrameworkIntegerArgType,
  isFrameworkNumberArgType,
  shouldPreserveRawDriveUnitPortArgument,
  shouldPreserveRawFrameworkEnumArgument,
  shouldPreserveRawFrameworkNumericArgument,
  shouldPreserveRawFrameworkStringArgument,
} from '../../../utils/frameworkArgTypes';
import { getLocalizedFrameworkArgLabel, getLocalizedFrameworkUnit } from '../../../utils/frameworkI18n';
import {
  canonicalizeSequenceTupleKeyValue,
  defaultSequenceTupleRowArgValues,
  isSequenceTupleArg,
  sequenceTupleKeyMember,
  sequenceTupleMaxRows,
  sequenceTupleMemberValueKey,
  sequenceTupleRowIndices,
  tupleMemberValueKey,
} from '../../../utils/tupleArg';
import { getRawActionArgValue } from '../../CodeEditor/ConfigurationTabs/driveModeAutonomousActionUtils';

const isFollowPathAction = (name: string | undefined): boolean => {
  return name === 'follow_path_action';
};

const isTupleType = (type: string | undefined): boolean => String(type || '').toLowerCase() === 'tuple';

const selectSx = {
  fontSize: 12,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderRadius: 8 },
} as const;

const textFieldSx = {
  '& .MuiInputBase-root': { fontSize: 12 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderRadius: 8 },
} as const;

function computeLabelWidthCh(actionDef: FrameworkAction | null | undefined, t: TFunction): number {
  const labels = (actionDef?.arguments ?? []).map((a) => getLocalizedFrameworkArgLabel(t, a.name));
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  return Math.min(Math.max(longest + 1, 8), 18);
}

function computeUnitWidthCh(actionDef: FrameworkAction | null | undefined, t: TFunction): number {
  const units = (actionDef?.arguments ?? []).flatMap((a) => {
    if (isTupleType(a.type)) return [];
    return [getLocalizedFrameworkUnit(t, (a as FrameworkArgument).units || '')];
  });
  const longest = units.reduce((max, unit) => Math.max(max, unit.length), 0);
  if (longest === 0) return 0;
  return Math.min(Math.max(longest + 1, 6), 16);
}

/** When set, drive-unit port arguments render `DriveUnitPortControl` instead of a numeric field. */
export type DriveUnitPortArgFieldContext = {
  editor?: monaco.editor.IStandaloneCodeEditor | null;
  lineNumber: number;
  syntheticIdPrefix: string;
  driveUnitPortRegistryRev: number;
  stubMechanismClass: FrameworkClass;
  stubParentClass?: FrameworkClass;
  portConflictPresentation: 'dialog' | 'inline';
  getOccupiedPortInlineMessage?: (argName: string) => string | null;
};

export interface FrameworkActionArgumentFieldsProps {
  actionDef: FrameworkAction | null | undefined;
  values: Record<string, string>;
  onArgChange: (name: string, value: string) => void;
  /**
   * Batch replacement of the whole `values` map. Required for add/remove of
   * sequence-of-tuples rows (which insert/delete multiple keys at once). When
   * omitted, the row add/remove controls are hidden; single-value edits still
   * flow through `onArgChange`.
   */
  onArgValuesChange?: (next: Record<string, string>) => void;
  pathOptions: Array<{ id: string; name: string }>;
  enumOptions: EnumOptionsMap;
  t: TFunction;
  /** Dialog: horizontal label | control | units. Drive mode: label above control. */
  layout?: 'row' | 'stack';
  showUnits?: boolean;
  /** When set, aligns columns across multiple instances (e.g. pressed vs released). */
  labelWidthCh?: number;
  unitWidthCh?: number;
  spacing?: number;
  idPrefix?: string;
  readOnly?: boolean;
  driveUnitPortArgFieldContext?: DriveUnitPortArgFieldContext | null;
}

const FrameworkActionArgumentFields: React.FC<FrameworkActionArgumentFieldsProps> = ({
  actionDef,
  values,
  onArgChange,
  onArgValuesChange,
  pathOptions,
  enumOptions,
  t,
  layout = 'row',
  showUnits = true,
  labelWidthCh: labelWidthChProp,
  unitWidthCh: unitWidthChProp,
  spacing = 1.5,
  idPrefix = 'arg',
  readOnly = false,
  driveUnitPortArgFieldContext = null,
}) => {
  const args = actionDef?.arguments;
  const labelWidthCh = labelWidthChProp ?? computeLabelWidthCh(actionDef, t);
  const unitWidthCh = unitWidthChProp ?? computeUnitWidthCh(actionDef, t);

  const argumentLabelSx = useMemo(() => ({ width: `${labelWidthCh}ch`, flexShrink: 0 }), [labelWidthCh]);
  const argumentUnitsSx = useMemo(
    () =>
      ({
        width: unitWidthCh > 0 ? `${unitWidthCh}ch` : 0,
        flexShrink: 0,
        color: 'text.secondary',
        whiteSpace: 'nowrap' as const,
      } as const),
    [unitWidthCh]
  );

  const isRow = layout === 'row';

  if (!args?.length) return null;

  /**
   * Renders the input control (path-select, enum-select, or number/text field)
   * for a single scalar `FrameworkArgument`. `valueKey` is the key used in the
   * `values` map and `onArgChange` callback — distinct from `arg.name` so tuple
   * members can use dotted keys like `color_at_min_input.red`.
   */
  const renderScalarControl = (arg: FrameworkArgument, valueKey: string): React.ReactNode => {
    const rawValue = String(values[valueKey] ?? '');
    const rawSourceValue = getRawActionArgValue(values, valueKey);
    const unsupportedStringRawValue = shouldPreserveRawFrameworkStringArgument(arg, rawValue, actionDef?.name, rawSourceValue)
      ? rawSourceValue || rawValue
      : undefined;

    if (isFollowPathAction(actionDef?.name) && arg.name === 'path') {
      const hasCurrentPathOption = pathOptions.some((opt) => opt.name === rawValue);
      const control = (
        <FormControl size="small" fullWidth disabled={readOnly}>
          <Select
            size="small"
            displayEmpty
            value={values[valueKey] ?? ''}
            onChange={(e) => onArgChange(valueKey, String(e.target.value))}
            sx={selectSx}
            renderValue={(val) => (val ? String(val) : t('controller.buttonMapping.selectPath', 'Select path'))}
          >
            {pathOptions.length === 0 && (
              <MenuItem value="" disabled>
                {t('controller.buttonMapping.noPathsAvailable', 'No paths available')}
              </MenuItem>
            )}
            {rawValue && !hasCurrentPathOption && (
              <MenuItem value={rawValue} disabled sx={{ fontSize: 12 }}>
                {rawValue}
              </MenuItem>
            )}
            {pathOptions.map((opt) => (
              <MenuItem key={opt.id} value={opt.name} sx={{ fontSize: 12 }}>
                {opt.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
      if (!unsupportedStringRawValue) return control;
      return (
        <Stack spacing={0.75} sx={{ width: '100%' }}>
          <UnsupportedCodeStatus
            code={unsupportedStringRawValue}
            descriptionI18nKey="controller.buttonMapping.unsupportedStringArgument.description"
            descriptionDefaultValue="Unsupported string argument: {{code}}"
          />
          {control}
        </Stack>
      );
    }

    const isNumber = isFrameworkNumberArgType(arg.type || '');
    const enumOpts = isNumber ? undefined : enumOptions[arg.type || ''];

    if (enumOpts?.length) {
      const unsupportedEnumRawValue = shouldPreserveRawFrameworkEnumArgument(arg, rawValue) ? rawValue : undefined;
      const selectValue = unsupportedEnumRawValue ? '' : values[valueKey] ?? enumOpts[0].value;
      const renderEnumValue = (val: unknown) => {
        const selectedValue = String(val ?? '');
        if (!selectedValue) return t('controller.buttonMapping.selectReplacement', 'Select replacement');
        return enumOpts.find((opt) => opt.value === selectedValue)?.label ?? selectedValue;
      };
      const control = (
        <FormControl size="small" fullWidth disabled={readOnly}>
          <Select
            size="small"
            displayEmpty
            value={selectValue}
            onChange={(e) => onArgChange(valueKey, String(e.target.value))}
            sx={selectSx}
            renderValue={renderEnumValue}
          >
            {unsupportedEnumRawValue ? (
              <MenuItem value="" disabled sx={{ fontSize: 12 }}>
                {t('controller.buttonMapping.selectReplacement', 'Select replacement')}
              </MenuItem>
            ) : null}
            {enumOpts.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
      if (!unsupportedEnumRawValue) return control;
      return (
        <Stack spacing={0.75} sx={{ width: '100%' }}>
          <UnsupportedCodeStatus
            code={unsupportedEnumRawValue}
            descriptionI18nKey="controller.buttonMapping.unsupportedEnumArgument.description"
            descriptionDefaultValue="Unsupported enum argument: {{code}}"
          />
          {control}
        </Stack>
      );
    }

    const numericError = getFrameworkNumericArgError(arg, rawValue, t);
    const unitLabel = arg.units ? getLocalizedFrameworkUnit(t, arg.units) : '';
    const rangeHelperText = getFrameworkNumericArgRangeText(arg, t, unitLabel);
    const unsupportedNumericRawValue = shouldPreserveRawFrameworkNumericArgument(arg, rawValue) ? rawValue : undefined;
    const unsupportedScalarRawValue = unsupportedNumericRawValue || unsupportedStringRawValue;
    const control = (
      <TextField
        size="small"
        fullWidth
        value={rawValue}
        type={isNumber && !unsupportedScalarRawValue ? 'number' : 'text'}
        disabled={readOnly}
        onChange={(e) => onArgChange(valueKey, e.target.value)}
        inputProps={
          isNumber && !unsupportedScalarRawValue
            ? {
                step: isFrameworkIntegerArgType(arg.type || '') ? 1 : 'any',
                ...(typeof arg.min === 'number' ? { min: arg.min } : {}),
                ...(typeof arg.max === 'number' ? { max: arg.max } : {}),
              }
            : undefined
        }
        sx={textFieldSx}
        error={!unsupportedScalarRawValue && Boolean(numericError)}
        helperText={!unsupportedScalarRawValue ? numericError ?? rangeHelperText ?? undefined : undefined}
      />
    );
    if (!unsupportedScalarRawValue) return control;
    return (
      <Stack spacing={0.75} sx={{ width: '100%' }}>
        <UnsupportedCodeStatus
          code={unsupportedScalarRawValue}
          descriptionI18nKey={
            unsupportedNumericRawValue
              ? 'controller.buttonMapping.unsupportedNumericArgument.description'
              : 'controller.buttonMapping.unsupportedStringArgument.description'
          }
          descriptionDefaultValue={unsupportedNumericRawValue ? 'Unsupported numeric argument: {{code}}' : 'Unsupported string argument: {{code}}'}
        />
        {control}
      </Stack>
    );
  };

  const renderTupleRow = (arg: FrameworkArgument, key: string, label: string): React.ReactNode => {
    const members = arg.members || [];
    const unsupportedTupleRawValue = getRawActionArgValue(values, arg.name);
    const memberControls = (
      <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
        <UnsupportedCodeStatus
          code={unsupportedTupleRawValue}
          descriptionI18nKey="controller.buttonMapping.unsupportedTupleArgument.description"
          descriptionDefaultValue="Unsupported tuple argument: {{code}}"
        />
        <Stack direction="row" spacing={1} sx={{ minWidth: 0 }}>
          {members.map((member) => (
            <Stack key={member.name} spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {getLocalizedFrameworkArgLabel(t, member.name)}
              </Typography>
              {renderScalarControl(member, tupleMemberValueKey(arg.name, member.name))}
            </Stack>
          ))}
        </Stack>
      </Stack>
    );

    if (isRow) {
      return (
        <Stack key={key} direction="row" alignItems="flex-start" spacing={1.5}>
          <Typography variant="body2" sx={{ ...argumentLabelSx, mt: 2 }}>
            {label}
          </Typography>
          {memberControls}
        </Stack>
      );
    }
    return (
      <Stack key={key} spacing={0.5} alignItems="stretch">
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{label}</Typography>
        {memberControls}
      </Stack>
    );
  };

  /**
   * Renders a sequence-of-tuples argument (e.g. `Sequence[tuple[int, str]]`) as a dynamic
   * list of rows. Each row reuses the per-member scalar controls; add/remove require the
   * `onArgValuesChange` batch setter (those controls are hidden without it).
   */
  const renderSequenceTupleRows = (arg: FrameworkArgument, key: string, label: string): React.ReactNode => {
    const members = arg.members || [];
    const rowIndices = sequenceTupleRowIndices(arg.name, values);
    const canEditRows = !readOnly && Boolean(onArgValuesChange);
    const rawValue = String(values[arg.name] ?? '').trim();
    const unsupportedRawValue = rowIndices.length === 0 && isUnsupportedSequenceTupleLiteral(rawValue, members) ? rawValue : undefined;

    // A bounded-integer key member (e.g. `line_number` 1–7) caps the row count to its range size
    // and must be unique across rows.
    const keyMember = sequenceTupleKeyMember(arg);
    const maxRows = sequenceTupleMaxRows(arg);
    const atMaxRows = maxRows !== undefined && rowIndices.length >= maxRows;
    // Canonicalize the key value the same way save validation does, so the inline duplicate error and
    // Add-row seeding agree with what disables Save (e.g. `1`, `01`, `1.0` are the same target line).
    const keyValueForRow = (rowIndex: number): string =>
      keyMember
        ? canonicalizeSequenceTupleKeyValue(keyMember, String(values[sequenceTupleMemberValueKey(arg.name, rowIndex, keyMember.name)] ?? ''))
        : '';
    const keyValueCounts = new Map<string, number>();
    if (keyMember) {
      rowIndices.forEach((rowIndex) => {
        const value = keyValueForRow(rowIndex);
        if (value) keyValueCounts.set(value, (keyValueCounts.get(value) ?? 0) + 1);
      });
    }
    const isDuplicateKeyRow = (rowIndex: number): boolean => {
      const value = keyValueForRow(rowIndex);
      return Boolean(value) && (keyValueCounts.get(value) ?? 0) > 1;
    };

    const handleAddRow = () => {
      if (!onArgValuesChange || atMaxRows) return;
      const nextIndex = rowIndices.length > 0 ? Math.max(...rowIndices) + 1 : 0;
      const usedKeyValues = new Set(rowIndices.map(keyValueForRow).filter(Boolean));
      const additions = defaultSequenceTupleRowArgValues(arg, nextIndex, usedKeyValues);
      const next = { ...values, ...additions };
      // Adding the first clean row replaces any preserved unsupported expression.
      delete next[arg.name];
      onArgValuesChange(next);
    };

    const handleRemoveRow = (rowIndex: number) => {
      if (!onArgValuesChange) return;
      const next = { ...values };
      members.forEach((member) => delete next[sequenceTupleMemberValueKey(arg.name, rowIndex, member.name)]);
      onArgValuesChange(next);
    };

    return (
      <Stack key={key} spacing={0.5} alignItems="stretch">
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{label}</Typography>
        {unsupportedRawValue ? (
          <UnsupportedCodeStatus
            code={unsupportedRawValue}
            descriptionI18nKey="frameworkActionArgs.sequenceTuple.unsupported"
            descriptionDefaultValue="Unsupported argument: {{code}}"
          />
        ) : rowIndices.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            {t('frameworkActionArgs.sequenceTuple.empty', { defaultValue: 'No rows yet' })}
          </Typography>
        ) : (
          rowIndices.map((rowIndex) => (
            <Stack key={`${key}-row-${rowIndex}`} direction="row" alignItems="flex-start" spacing={1}>
              <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                {members.map((member) => {
                  const showDuplicateError = Boolean(keyMember) && member.name === keyMember?.name && isDuplicateKeyRow(rowIndex);
                  return (
                    <Stack key={member.name} spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        {getLocalizedFrameworkArgLabel(t, member.name)}
                      </Typography>
                      {renderScalarControl(member, sequenceTupleMemberValueKey(arg.name, rowIndex, member.name))}
                      {showDuplicateError && (
                        <Typography variant="caption" color="error">
                          {t('frameworkActionArgs.sequenceTuple.duplicateKey', {
                            label: getLocalizedFrameworkArgLabel(t, member.name),
                            defaultValue: '{{label}} must be unique',
                          })}
                        </Typography>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
              {canEditRows && (
                <IconButton
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => handleRemoveRow(rowIndex)}
                  aria-label={t('frameworkActionArgs.sequenceTuple.removeRow', {
                    index: rowIndex + 1,
                    defaultValue: 'Remove row {{index}}',
                  })}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))
        )}
        {canEditRows && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={handleAddRow}
              disabled={atMaxRows}
              sx={{ fontSize: 12, textTransform: 'none' }}
            >
              {t('frameworkActionArgs.sequenceTuple.add', { defaultValue: 'Add' })}
            </Button>
            {atMaxRows && (
              <Typography variant="caption" color="text.secondary">
                {t('frameworkActionArgs.sequenceTuple.maxRows', { count: maxRows, defaultValue: 'Maximum {{count}} rows' })}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <Stack spacing={spacing}>
      {args.map((arg) => {
        const frameworkArg = arg as FrameworkArgument;
        const key = `${idPrefix}-${arg.name}`;
        const label = getLocalizedFrameworkArgLabel(t, arg.name);

        if (isSequenceTupleArg(frameworkArg)) {
          return renderSequenceTupleRows(frameworkArg, key, label);
        }

        if (isTupleType(arg.type)) {
          return renderTupleRow(frameworkArg, key, label);
        }

        const unitsText = frameworkArg.units ? getLocalizedFrameworkUnit(t, frameworkArg.units) : '';
        const unitsEl =
          isRow && showUnits ? (
            <Typography variant="body2" sx={argumentUnitsSx}>
              {unitsText}
            </Typography>
          ) : null;

        if (driveUnitPortArgFieldContext && isDriveUnitPortFrameworkArgument(frameworkArg)) {
          const rawValue = String(values[arg.name] ?? '');
          const unsupportedRawValue = shouldPreserveRawDriveUnitPortArgument(frameworkArg, rawValue) ? rawValue : undefined;
          const parsedPort = unsupportedRawValue ? Number.NaN : Number.parseInt(rawValue, 10);
          const portValue = Number.isFinite(parsedPort) && parsedPort >= 1 && parsedPort <= 8 ? parsedPort : undefined;
          const electronicId = `${driveUnitPortArgFieldContext.syntheticIdPrefix}:${arg.name}`;
          const occupiedMessage = driveUnitPortArgFieldContext.getOccupiedPortInlineMessage?.(arg.name) ?? null;
          const portDeviceProps = {
            id: electronicId,
            header: '',
            editor: driveUnitPortArgFieldContext.editor ?? undefined,
            initialOpen: true,
            lineNumber: driveUnitPortArgFieldContext.lineNumber,
            parameterName: arg.name,
            parentName: '__pending_action_arg__',
            mechanismName: '__pending_action_arg__',
            parentClass: driveUnitPortArgFieldContext.stubParentClass,
            mechanismClass: driveUnitPortArgFieldContext.stubMechanismClass,
            driveUnitPortRegistryRev: driveUnitPortArgFieldContext.driveUnitPortRegistryRev,
          };
          const control = (
            <Stack spacing={1} alignItems="stretch">
              <DriveUnitPortControl
                {...portDeviceProps}
                portControlValue={portValue}
                setPortControlValue={(n) => onArgChange(arg.name, String(n))}
                skipEditorParameterSync
                portConflictPresentation={driveUnitPortArgFieldContext.portConflictPresentation}
                unsupportedRawValue={unsupportedRawValue}
                labelId={`${idPrefix}-${arg.name}-port-label`}
                label={label}
                selectId={`${idPrefix}-${arg.name}-port`}
                selectLabelId={`${idPrefix}-${arg.name}-port-select-label`}
              />
              {occupiedMessage ? (
                <Typography variant="body2" color="text.warning">
                  {occupiedMessage}
                </Typography>
              ) : null}
            </Stack>
          );

          if (isRow) {
            return (
              <Stack key={key} direction="column" spacing={0.75} alignItems="stretch">
                <Typography variant="body2" sx={argumentLabelSx}>
                  {label}
                </Typography>
                {control}
                {unitsEl}
              </Stack>
            );
          }
          return (
            <Stack key={key} spacing={0.5} alignItems="stretch">
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{label}</Typography>
              {control}
              {showUnits && unitsText ? (
                <Typography variant="caption" color="text.secondary">
                  {unitsText}
                </Typography>
              ) : null}
            </Stack>
          );
        }

        const control = renderScalarControl(frameworkArg, arg.name);

        if (isRow) {
          return (
            <Stack key={key} direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="body2" sx={argumentLabelSx}>
                {label}
              </Typography>
              {control}
              {unitsEl}
            </Stack>
          );
        }
        return (
          <Stack key={key} spacing={0.5} alignItems="stretch">
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{label}</Typography>
            {control}
            {showUnits && unitsText ? (
              <Typography variant="caption" color="text.secondary">
                {unitsText}
              </Typography>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default FrameworkActionArgumentFields;
